import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Container, Paper, Typography, Button, Box, Chip, Divider,
  LinearProgress, IconButton, List, ListItem, ListItemText,
  ListItemSecondaryAction, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, CreditCard, Delete, CheckCircle, Group } from '@mui/icons-material';
import { useApp } from '../context/AppContext';
import { planCards } from '../data/plans';
import { TEAM_MEMBER_POLL_INTERVAL_MS } from '../utils/constants';
import { colors, radii, fonts } from '../theme/colors';
import PlanCard from './PlanCard';

/**
 * Subscription / billing management page. Rendered when `currentPage` is
 * 'billing' (set from the account menu in ConversionPage). Three logical
 * regions:
 *
 *   1. Subscription summary card — current plan, status chip, period end,
 *      saved card brand + last4, monthly conversion usage.
 *   2. Plan switcher — grid of `PlanCard`s with a monthly/yearly toggle.
 *      Upgrades/downgrades route through `handleCheckout` from AppContext,
 *      which opens a confirmation dialog before calling
 *      `/api/subscription/change`.
 *   3. Team management (Team plan only) — list of seats with add/remove,
 *      polled every `TEAM_MEMBER_POLL_INTERVAL_MS` while invites are pending.
 *
 * Takes no props — all state from `useApp()`. The "Manage in Stripe" button
 * delegates to `handleManageAccount` which opens the Stripe Billing Portal.
 */
export default function BillingPortal() {
  const {
    API_BASE_URL, token, planType, subscriptionStatus, currentPeriodEnd,
    cardLast4, cardBrand, monthlyConversionsUsed, monthlyConversionLimit,
    conversionsRemaining, monthlyResetDate,
    setCurrentPage, setConfirmDialog, setSnackbar,
    handleCancelSubscription, handleManageAccount, handleCheckout, handleChangePlan,
  } = useApp();

  const [billingInterval, setBillingInterval] = useState('month');

  // Team management state (billing page only)
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [teamError, setTeamError] = useState('');
  const [teamSuccess, setTeamSuccess] = useState('');
  const [teamManagementOpen, setTeamManagementOpen] = useState(false);

  const fetchTeamMembers = useCallback(async () => {
    if (!token || !planType?.includes('team')) return;
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/team/members`, { headers: { Authorization: `Bearer ${token}` } });
      setTeamMembers(resp.data.members || []);
    } catch (_) {}
  }, [token, planType, API_BASE_URL]);

  useEffect(() => { fetchTeamMembers(); }, [fetchTeamMembers]);

  // Poll for pending team member status changes
  useEffect(() => {
    if (!teamMembers.length || !teamMembers.some(m => m.status === 'pending')) return;
    const intervalId = setInterval(fetchTeamMembers, TEAM_MEMBER_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [teamMembers, fetchTeamMembers]);

  const handleAddTeamMember = async () => {
    if (!newMemberEmail) return;
    setIsAddingMember(true); setTeamError(''); setTeamSuccess('');
    try {
      await axios.post(`${API_BASE_URL}/api/team/add`, { email: newMemberEmail }, { headers: { Authorization: `Bearer ${token}` } });
      setTeamSuccess(`Invitation sent to ${newMemberEmail}. NOTE: Please ask them to check their SPAM folder.`);
      setNewMemberEmail(''); fetchTeamMembers();
    } catch (err) {
      setTeamError(err.response?.data?.error || 'Failed to add member');
    } finally { setIsAddingMember(false); }
  };

  const handleRemoveTeamMember = (memberEmail) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Team Member',
      message: `Are you sure you want to remove ${memberEmail}? They will lose access immediately.`,
      onConfirm: async () => {
        try {
          await axios.post(`${API_BASE_URL}/api/team/remove`, { email: memberEmail }, { headers: { Authorization: `Bearer ${token}` } });
          setSnackbar({ open: true, message: 'Member removed successfully', severity: 'success' });
          fetchTeamMembers();
        } catch (err) {
          setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to remove member', severity: 'error' });
        }
      }
    });
  };

  const handleResendInvite = async (memberEmail) => {
    try {
      await axios.post(`${API_BASE_URL}/api/team/resend`, { email: memberEmail }, { headers: { Authorization: `Bearer ${token}` } });
      setSnackbar({ open: true, message: `Invite resent to ${memberEmail}. Tell them to check SPAM folder.`, severity: 'success' });
    } catch (_) {
      setSnackbar({ open: true, message: 'Failed to resend invite', severity: 'error' });
    }
  };

  const getPlanDetails = (type) => {
    if (!type || type === 'free') return { name: 'Free Tier', price: '$0', desc: '10 conversions/month' };
    if (type === 'monthly') return { name: 'Legacy Monthly', price: '$10/mo', desc: '500 conversions/month' };
    if (type === 'yearly') return { name: 'Legacy Yearly', price: '$80/yr', desc: '1,000 conversions/month' };
    const baseId = type.replace('_yearly', '');
    const isYearly = type.endsWith('_yearly');
    const card = planCards.find(p => p.id === baseId);
    if (card) return { name: `${card.name} ${isYearly ? 'Yearly' : 'Monthly'}`, price: isYearly ? card.yearlyPrice : card.price, desc: card.features[0] };
    return { name: type, price: '', desc: '' };
  };

  const details = getPlanDetails(planType);
  const isYearly = billingInterval === 'year';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: `${radii.card}px`, bgcolor: colors.surface, border: '1px solid', borderColor: colors.borderSubtle }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => setCurrentPage('converter')} sx={{ mr: 2 }}><ArrowBack /></IconButton>
          <Typography variant="h4" component="h1">Billing Portal</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Current Subscription */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Current Subscription</Typography>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${radii.panel}px`, bgcolor: colors.paper, borderColor: colors.borderSubtle }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body1" fontWeight="bold">{details.name}</Typography>
              <Chip label={subscriptionStatus === 'active' ? 'Active' : subscriptionStatus || 'Free'}
                color={subscriptionStatus === 'active' ? 'success' : 'default'} size="small" />
            </Box>
            {planType !== 'free' && (
              <>
                <Typography variant="body2" color="text.secondary">{details.price} - {details.desc}</Typography>
                {currentPeriodEnd && (
                  <Typography variant="body2" color="text.secondary">
                    Next billing date: {new Date(currentPeriodEnd * 1000).toLocaleDateString()}
                  </Typography>
                )}
                {cardLast4 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CreditCard sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {cardBrand ? cardBrand.charAt(0).toUpperCase() + cardBrand.slice(1) : 'Card'} ending in {cardLast4}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>Monthly Usage</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress variant="determinate"
                    value={Math.min(100, (monthlyConversionsUsed / monthlyConversionLimit) * 100)}
                    color={conversionsRemaining > 10 ? 'primary' : conversionsRemaining > 0 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100, textAlign: 'right' }}>
                  {monthlyConversionsUsed} / {monthlyConversionLimit} used
                </Typography>
              </Box>
              {monthlyResetDate && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Resets on {new Date(monthlyResetDate).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Team Management Button (team plan only) */}
        {planType?.includes('team') && (
          <Box sx={{ mb: 4 }}>
            <Button variant="outlined" startIcon={<Group />} onClick={() => setTeamManagementOpen(true)}>
              Manage Team Members
            </Button>
          </Box>
        )}

        {/* Available Plans */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Available Plans</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: colors.paper, border: `1px solid ${colors.borderSubtle}`, p: 0.5, borderRadius: `${radii.pill}px` }}>
              <Button size="small" variant={billingInterval === 'month' ? 'contained' : 'text'} color="secondary"
                onClick={() => setBillingInterval('month')}
                sx={{ borderRadius: `${radii.pill}px`, px: 2.5, ...(billingInterval !== 'month' && { color: colors.textSecondary }) }}>
                Monthly
              </Button>
              <Button size="small" variant={billingInterval === 'year' ? 'contained' : 'text'} color="secondary"
                onClick={() => setBillingInterval('year')}
                sx={{ borderRadius: `${radii.pill}px`, px: 2.5, ...(billingInterval !== 'year' && { color: colors.textSecondary }) }}>
                Yearly (-10%)
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
            {planCards.map((plan) => {
              const planIdForAction = (isYearly && plan.id !== 'free') ? `${plan.id}_yearly` : plan.id;
              const isCurrent = planType === planIdForAction || (plan.id === 'free' && (!planType || planType === 'free'));
              const displayPlan = {
                ...plan,
                price: isYearly && plan.yearlyPrice ? plan.yearlyPrice : plan.price,
                period: isYearly && plan.yearlyPeriod ? plan.yearlyPeriod : plan.period,
              };
              return (
                <PlanCard key={plan.id} plan={displayPlan}
                  billingPeriod={isYearly ? 'yearly' : 'monthly'}
                  ctaOverride={isCurrent ? <Chip label="Current Plan" color="primary" size="small" /> : undefined}
                  onSelect={(id) => {
                    const targetId = (isYearly && id !== 'free') ? `${id}_yearly` : id;
                    if (targetId === 'free') {
                      setConfirmDialog({ open: true, title: 'Cancel Subscription',
                        message: 'Are you sure you want to cancel? You will be downgraded to the free tier.',
                        onConfirm: handleCancelSubscription });
                    } else if (planType === 'free' || !planType) {
                      handleCheckout(targetId);
                    } else {
                      setConfirmDialog({ open: true,
                        title: `Switch to ${plan.name} (${isYearly ? 'Yearly' : 'Monthly'})`,
                        message: `Switch to the ${plan.name} ${isYearly ? 'yearly' : 'monthly'} plan? Your billing will be prorated.`,
                        onConfirm: () => handleChangePlan(targetId)
                      });
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Manage Payment Methods */}
        {planType !== 'free' && (
          <Box>
            <Button variant="text" onClick={handleManageAccount} startIcon={<CreditCard />}>
              Manage Payment Methods or Cancel Subscription
            </Button>
          </Box>
        )}
      </Paper>

      {/* Team Management Dialog */}
      <Dialog open={teamManagementOpen} onClose={() => setTeamManagementOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Team Management</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Manage your team members. You can add up to 10 members who will receive unlimited conversions.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 2 }}>
            <TextField size="small" placeholder="Enter email address" value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)} fullWidth
              error={Boolean(teamError)} helperText={teamError || teamSuccess}
              FormHelperTextProps={{ sx: { color: teamError ? 'error.main' : 'success.main' } }} />
            <Button variant="contained" onClick={handleAddTeamMember}
              disabled={isAddingMember || !newMemberEmail} sx={{ whiteSpace: 'nowrap' }}>
              {isAddingMember ? 'Adding...' : 'Add'}
            </Button>
          </Box>
          <List sx={{ bgcolor: colors.surface, borderRadius: `${radii.panel}px`, border: '1px solid', borderColor: colors.borderSubtle }}>
            {teamMembers.map((member, index) => (
              <ListItem key={index} divider={index < teamMembers.length - 1}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span" sx={{ fontFamily: fonts.mono, fontSize: '0.875rem' }}>{member.email}</Box>
                      {member.status === 'verified'
                        ? <Chip label="Verified" size="small" color="success" icon={<CheckCircle sx={{ fontSize: 14 }} />} />
                        : <Chip label="Pending" size="small" color="warning" variant="outlined" />
                      }
                    </Box>
                  }
                  secondary={`Added: ${new Date(member.added_at).toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  {member.status === 'pending' && (
                    <Button size="small" onClick={() => handleResendInvite(member.email)} sx={{ mr: 1 }}>Resend</Button>
                  )}
                  <IconButton edge="end" onClick={() => handleRemoveTeamMember(member.email)} color="error">
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {teamMembers.length === 0 && (
              <ListItem><ListItemText secondary="No team members yet. Add someone above!" /></ListItem>
            )}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {teamMembers.length} / 10 members used
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamManagementOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
